import React, { useState } from "react";
import dayjs from "dayjs";
import { Truck, Package, Box, Wrench, Users, ShoppingCart, Clock, Ship, Calendar, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { DatePicker } from "antd";
import type { Dayjs } from "dayjs";
import { useGetTodayMetrics } from "~/services/service-ticket";
import { useGetExpenseStats } from "~/services/expense";
import { useAuth } from "~/store";
import { Role } from "~/utils/constants";

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

interface MetricsProps {
  scheduleDate?: Dayjs;
  onDateSync?: () => void;
}

export const Metrics: React.FC<MetricsProps> = ({ scheduleDate, onDateSync }) => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<string>(dayjs().format("YYYY-MM-DD"));
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Only show for super admins and system admins
  if (user?.role !== Role.SUPER_ADMIN && user?.role !== Role.SYSTEM_ADMIN) {
    return null;
  }
  
  // Get metrics for selected date from backend
  const { data: metricsData, isLoading } = useGetTodayMetrics(selectedDate);

  // Expense stats
  const { data: expenseStats } = useGetExpenseStats();

  // Use backend data or defaults
  const metrics = metricsData || {
    totalStaffWorking: 0,
    transport: { count: 0, revenue: 0, cost: 0 },
    site: { count: 0, revenue: 0, cost: 0 },
    fabrication: { count: 0, revenue: 0, cost: 0 },
    warehouse: { count: 0, revenue: 0, cost: 0 },
    totalMaterialCharges: 0,
    totalStaffHours: 0,
    totalVesselHours: 0,
    totalStaffCost: 0,
    totalVesselCost: 0,
    totalMaterialsCost: 0,
    totalDirectCosts: 0,
    totalRevenue: 0,
    totalCost: 0,
    profit: 0,
  };

  // Calculate total direct expenses (staff + vessel + materials + other expenses)
  const otherExpenses = expenseStats?.paidAmount || 0;
  const totalDirectExpenses = metrics.totalDirectCosts + otherExpenses;
  const profit = metrics.totalRevenue - totalDirectExpenses;

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border p-6">
        <h3 className="font-medium mb-4">Today's Metrics</h3>
        <div className="flex items-center justify-center h-32">
          <div className="text-gray-500">Loading metrics...</div>
        </div>
      </div>
    );
  }

  const isToday = selectedDate === dayjs().format("YYYY-MM-DD");
  const displayDate = dayjs(selectedDate);
  const dateLabel = isToday 
    ? "Today's Metrics" 
    : displayDate.format("MMM D, YYYY");

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSelectedDate(date.format("YYYY-MM-DD"));
    }
  };

  const handlePreviousDay = () => {
    const prevDate = dayjs(selectedDate).subtract(1, "day");
    if (prevDate.isBefore(dayjs(), "day") || prevDate.isSame(dayjs(), "day")) {
      setSelectedDate(prevDate.format("YYYY-MM-DD"));
    }
  };

  const handleNextDay = () => {
    const nextDate = dayjs(selectedDate).add(1, "day");
    setSelectedDate(nextDate.format("YYYY-MM-DD"));
  };

  const handleSyncToSchedule = () => {
    if (scheduleDate) {
      setSelectedDate(scheduleDate.format("YYYY-MM-DD"));
    } else {
      // Fallback to today if no schedule date provided
      setSelectedDate(dayjs().format("YYYY-MM-DD"));
    }
    if (onDateSync) {
      onDateSync();
    }
  };

  const handleQuickDate = (days: number) => {
    const targetDate = dayjs().add(days, "day");
    setSelectedDate(targetDate.format("YYYY-MM-DD"));
  };

  const canGoPrevious = dayjs(selectedDate).isAfter(dayjs().subtract(30, "day"), "day");
  // Allow going forward indefinitely
  const canGoNext = true;

  return (
    <div className="bg-white rounded-lg border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">{dateLabel}</h3>
        <div className="flex items-center gap-2">
          {/* Quick date buttons */}
          <div className="flex items-center gap-1 border border-gray-300 rounded-md overflow-hidden">
            <button
              onClick={() => handleQuickDate(-1)}
              className="px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 border-r border-gray-300"
              title="Yesterday"
            >
              Yesterday
            </button>
            <button
              onClick={() => handleQuickDate(0)}
              className="px-2 py-1.5 text-xs text-gray-600 hover:bg-gray-100 border-r border-gray-300"
              title="Today"
            >
              Today
            </button>
          </div>

          {/* Date navigation */}
          <div className="flex items-center gap-1 border border-gray-300 rounded-md overflow-hidden">
            <button
              onClick={handlePreviousDay}
              disabled={!canGoPrevious}
              className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border-r border-gray-300"
              title="Previous day"
            >
              <ChevronLeft size={16} />
            </button>
            
            <DatePicker
              value={dayjs(selectedDate)}
              onChange={handleDateChange}
              format="MMM D, YYYY"
              open={datePickerOpen}
              onOpenChange={setDatePickerOpen}
              className="!border-0 !shadow-none hover:!bg-gray-50"
              style={{ width: 140 }}
              suffixIcon={<Calendar size={16} className="text-gray-500" />}
              popupClassName="metrics-date-picker"
            />
            
            <button
              onClick={handleNextDay}
              disabled={!canGoNext}
              className="p-1.5 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed border-l border-gray-300"
              title="Next day"
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Sync button */}
          {scheduleDate && (
            <button
              onClick={handleSyncToSchedule}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-md transition-colors"
              title={`Sync to schedule date: ${scheduleDate.format("MMM D, YYYY")}`}
            >
              <RefreshCw size={14} />
              <span>Sync</span>
            </button>
          )}
        </div>
      </div>
      <div className="flex gap-4 items-start">
        {/* Service Metrics - 4 columns, 2 rows */}
        <div className="flex-1 space-y-2">
          {/* First Row */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Users size={14} className="text-gray-600" />
                <div className="text-[10px] text-gray-500">Staff Working</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">{metrics.totalStaffWorking}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Truck size={14} className="text-blue-600" />
                <div className="text-[10px] text-gray-500">Transport</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">
                {formatCurrency(metrics.transport.revenue)}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {metrics.transport.count} svc{metrics.transport.count !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Package size={14} className="text-purple-600" />
                <div className="text-[10px] text-gray-500">Site Services</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">
                {formatCurrency(metrics.site.revenue)}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {metrics.site.count} svc{metrics.site.count !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Box size={14} className="text-amber-600" />
                <div className="text-[10px] text-gray-500">Warehouse</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">
                {formatCurrency(metrics.warehouse.revenue)}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {metrics.warehouse.count} svc{metrics.warehouse.count !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
          {/* Second Row */}
          <div className="grid grid-cols-4 gap-2">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Clock size={14} className="text-gray-600" />
                <div className="text-[10px] text-gray-500">Staff Hours</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">{metrics.totalStaffHours.toFixed(1)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {metrics.transport.count + metrics.site.count + metrics.fabrication.count + metrics.warehouse.count} svcs
                {metrics.totalVesselHours > 0 && " • w/ travel"}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Ship size={14} className="text-gray-600" />
                <div className="text-[10px] text-gray-500">Vessel Hours</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">{metrics.totalVesselHours.toFixed(1)}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">w/ travel</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <ShoppingCart size={14} className="text-green-600" />
                <div className="text-[10px] text-gray-500">Material Charges</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">
                {formatCurrency(metrics.totalMaterialCharges)}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">billed</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center gap-1.5 mb-1">
                <Wrench size={14} className="text-red-600" />
                <div className="text-[10px] text-gray-500">Fabrication</div>
              </div>
              <div className="text-xl font-semibold text-gray-900">
                {formatCurrency(metrics.fabrication.revenue)}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {metrics.fabrication.count} svc{metrics.fabrication.count !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        </div>

        {/* Financial Summary Table - Right side, scaled to match grid height */}
        <div className="flex-shrink-0 w-80">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 pl-0 pr-4 text-[10px] font-semibold text-gray-700 uppercase tracking-wide">Metric</th>
                <th className="text-right py-2 px-4 text-[10px] font-semibold text-gray-700 uppercase tracking-wide">Value</th>
                <th className="text-left py-2 pl-4 pr-0 text-[10px] font-semibold text-gray-700 uppercase tracking-wide">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr className="hover:bg-gray-50">
                <td className="py-2.5 pl-0 pr-4 text-xs text-gray-700 font-medium">Est. Revenue</td>
                <td className="py-2.5 px-4 text-right text-sm font-semibold text-gray-900">{formatCurrency(metrics.totalRevenue)}</td>
                <td className="py-2.5 pl-4 pr-0 text-[10px] text-gray-500">{isToday ? "today" : "services"}</td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="py-2.5 pl-0 pr-4 text-xs text-gray-700 font-medium">Direct Expenses</td>
                <td className="py-2.5 px-4 text-right text-sm font-semibold text-gray-900">{formatCurrency(totalDirectExpenses)}</td>
                <td className="py-2.5 pl-4 pr-0 text-[10px] text-gray-500">
                  Staff: {formatCurrency(metrics.totalStaffCost)} • 
                  Vessel: {formatCurrency(metrics.totalVesselCost)} • 
                  Mat: {formatCurrency(metrics.totalMaterialsCost)}
                  {otherExpenses > 0 && ` • Other: ${formatCurrency(otherExpenses)}`}
                </td>
              </tr>
              <tr className="hover:bg-gray-50">
                <td className="py-2.5 pl-0 pr-4 text-xs text-gray-700 font-medium">Est. Profit</td>
                <td className={`py-2.5 px-4 text-right text-sm font-semibold ${profit >= 0 ? "text-gray-900" : "text-red-600"}`}>
                  {formatCurrency(profit)}
                </td>
                <td className="py-2.5 pl-4 pr-0 text-[10px] text-gray-500">{isToday ? "today" : ""}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};